import { AlertTriangle, Clock, Mail, MessageSquare, Pause, Play, ShieldCheck, Trash2 } from 'lucide-react';
import { useState } from 'react';
import type { AutomationDraft } from '../../api/comms';
import { ApiError } from '../../api/envelope';
import type {
  AutomationPreview,
  AutomationRun,
  AutomationTrigger,
  CampaignAutomation,
  CampaignBookingCount,
  CampaignChannel,
  Inbox,
} from '../../api/types';
import { identityLine, needsReauth } from '../../lib/channelMeta';
import { useTimeFormat } from '../../lib/tenantTime';
import { useLoad } from '../../lib/useLoad';
import { AUTOMATION_PRESETS, type AutomationPreset } from './automationPresets';
import {
  alwaysTitle,
  type AutomationProblem,
  automationProblem,
  lastRunLine,
  sinceLabel,
  triggerLine,
  waitTitle,
  WHEN_TITLES,
  whenDetail,
} from './automationTools';
import { AutomationsArt, FirstBookingArt, QuietArt, TripArt } from './campaignArt';
import { clockToMinutes, FREQUENCY_CAPS, minutesToClock } from './campaignTools';
import { useComms } from './context';
import { RichText, type RichTextValue } from './RichText';
import { useConfigNav } from './useConfigNav';
import { useModuleRefresh } from './useModuleRefresh';

/**
 * Automated campaigns: a message that sends itself when something happens in
 * dispatch. Each quarter hour the service gathers who newly qualifies and sends
 * them an ordinary campaign — a "run" — so every run has its own Results, and
 * quiet hours, the frequency cap, opt-outs and the mailbox's daily allowance all
 * apply exactly as they do to a campaign sent by hand.
 *
 * The rungs are the campaign ones: `view` reads, `draft` writes, and switching
 * one ON is the send decision, made once for every message it will ever send.
 */

const TRIGGERS: { key: AutomationTrigger; label: string; hint: string; art: typeof TripArt }[] = [
  { key: 'trip_completed', label: 'After a trip', hint: 'Thank-you or review request', art: TripArt },
  { key: 'first_booking', label: 'After a first reservation', hint: 'A welcome, once ever', art: FirstBookingArt },
  { key: 'lapsed', label: 'When a customer goes quiet', hint: 'Win them back', art: QuietArt },
];
const ART: Record<AutomationTrigger, typeof TripArt> = {
  trip_completed: TripArt,
  first_booking: FirstBookingArt,
  lapsed: QuietArt,
};

const STATUS_CHIP: Record<CampaignAutomation['status'], { bg: string; fg: string; label: string }> = {
  draft: { bg: 'var(--c-secondary)', fg: 'var(--c-muted)', label: 'Draft' },
  active: { bg: 'var(--c-green-muted)', fg: 'var(--c-green)', label: 'On' },
  paused: { bg: 'var(--c-orange-muted)', fg: 'var(--c-orange)', label: 'Paused' },
};

/**
 * What the editor opens on: an automation by id (with its name, for the
 * breadcrumb before it loads), or a new one — blank, or started from a preset.
 */
export type AutomationOpen = { id: number; name?: string } | { id: null; preset?: AutomationTrigger };

/** Campaigns at Send switches one on; Draft writes. Permissive while /meta loads. */
function useAutomationRights() {
  const { capabilities } = useComms();
  const level = capabilities?.access.features.campaigns ?? 'full';
  return { mayDraft: level === 'draft' || level === 'full', maySend: level === 'full' };
}

/**
 * Why an automation that is on is not sending — and the one button that deals
 * with it. A banner rather than a red line inside the form: the fix is almost
 * never in the form (a mailbox to sign in again, a person who can send to switch
 * it on), and the people waiting are the point.
 */
function ProblemBanner({
  automation,
  problem,
  inbox,
  onOpenRun,
  onFixed,
}: {
  automation: CampaignAutomation;
  problem: AutomationProblem;
  inbox: Inbox | undefined;
  onOpenRun: (campaignId: number) => void;
  onFixed: () => void;
}) {
  const { source } = useComms();
  const configNav = useConfigNav();
  const { maySend } = useAutomationRights();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const what = automation.channel === 'sms' ? 'number' : 'mailbox';
  const waiting =
    problem.waiting !== null
      ? ` ${problem.waiting.toLocaleString()} ${problem.waiting === 1 ? 'person is' : 'people are'} waiting — they are reached once it is fixed.`
      : problem.kind === 'dispatch' || problem.kind === 'run_failed'
        ? ''
        : ' Nobody is lost — the people it would have reached are still waiting.';

  // Somebody who can send from there taking it over: one call, the checks a
  // switch-on asks, and nothing changed if it is refused. It stays on, so the
  // people who qualified while it could not send are still reached.
  const switchOnAsMe = async () => {
    setBusy(true);
    setError(null);
    try {
      await source.api.activateAutomation(automation.id);
      onFixed();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Something went wrong.');
    } finally {
      setBusy(false);
    }
  };

  let action: { label: string; run: () => void } | null = null;
  if (problem.kind === 'reconnect' && inbox) {
    action = { label: `Reconnect ${what}`, run: () => configNav(`/inboxes/${inbox.id}`) };
  } else if (problem.kind === 'access' && maySend) {
    action = { label: 'Switch it on as you', run: () => void switchOnAsMe() };
  } else if (problem.kind === 'run_failed' && automation.lastRun) {
    const runId = automation.lastRun.id;
    action = { label: 'See the run', run: () => onOpenRun(runId) };
  }
  return (
    <div className="auto-problem" role="status">
      <AlertTriangle size={15} />
      <span className="auto-problem-text">
        <b>{problem.title}:</b> {problem.detail}
        {waiting}
        {error && <span className="auto-problem-error"> {error}</span>}
      </span>
      {action && (
        <button type="button" className="cbtn" disabled={busy} onClick={action.run}>
          {busy && <span className="btn-spin" />}
          {action.label}
        </button>
      )}
    </div>
  );
}

function Switch({
  on,
  label,
  disabled,
  onToggle,
}: {
  on: boolean;
  label: string;
  disabled?: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      className={`auto-switch${on ? ' on' : ''}`}
      disabled={disabled}
      onClick={onToggle}
    >
      <span />
    </button>
  );
}

/**
 * Automations: what each one does and has done, switched on and off in place,
 * and the three most operators want first to start from.
 */
export function CampaignAutomations({
  onOpen,
  onOpenRun,
}: {
  onOpen: (open: AutomationOpen) => void;
  onOpenRun: (campaignId: number) => void;
}) {
  const { source, inboxes, agents } = useComms();
  const { fmtDate } = useTimeFormat();
  const { mayDraft, maySend } = useAutomationRights();
  const [nonce, setNonce] = useState(0);
  const list = useLoad(() => source.api.listAutomations(), [source, nonce]);
  // Bookings are a dispatch read of their own: the cards never wait on it.
  const booked = useLoad(() => source.api.listAutomationBookings(), [source, nonce]);
  useModuleRefresh(() => setNonce((n) => n + 1));
  const [toggling, setToggling] = useState<number | null>(null);
  const [toggleError, setToggleError] = useState<{ id: number; message: string } | null>(null);

  const since = (iso: string | null) => sinceLabel(iso, (value) => fmtDate(value));
  const nameOf = (id: number) => agents.find((agent) => agent.dashBoardId === id)?.name ?? null;
  const bookedOf = (id: number): CampaignBookingCount | undefined => booked.data?.find((row) => row.id === id);

  const toggle = async (automation: CampaignAutomation) => {
    setToggling(automation.id);
    setToggleError(null);
    try {
      if (automation.status === 'active') await source.api.pauseAutomation(automation.id);
      else await source.api.activateAutomation(automation.id);
    } catch (e) {
      setToggleError({ id: automation.id, message: e instanceof ApiError ? e.message : 'Something went wrong.' });
    } finally {
      setToggling(null);
      setNonce((n) => n + 1);
    }
  };

  const rows = list.data ?? [];
  return (
    <div className="auto">
      <section className="auto-hero">
        <AutomationsArt className="auto-hero-art" />
        <div className="auto-hero-text">
          <h2>Messages that send themselves</h2>
          <p>
            Something happens in dispatch, you wait a while, and the right message goes out. Each person
            gets it once per trip — or once ever, for a welcome — under the same hours, opt-outs and limits
            as any campaign.
          </p>
        </div>
        {mayDraft && (
          <div className="auto-starts">
            <span className="auto-eyebrow">Start from</span>
            {TRIGGERS.map(({ key, label, hint, art: Art }) => (
              <button
                key={key}
                type="button"
                className="auto-start"
                title={`Opens a new automation: ${AUTOMATION_PRESETS[key].name}`}
                onClick={() => onOpen({ id: null, preset: key })}
              >
                <Art className="auto-start-art" />
                <span>
                  <b>{label}</b>
                  <span className="auto-muted">{hint}</span>
                </span>
              </button>
            ))}
          </div>
        )}
      </section>

      <section className="auto-yours" aria-label="Your automations">
        <div className="auto-section-head">
          <h2>Your automations</h2>
          <span>Checked every 15 minutes · totals across every run</span>
        </div>
        {list.error && <div className="cmg-insight bad">Could not load automations.</div>}
        {!list.error && list.data && rows.length === 0 && (
          <div className="auto-empty">
            None yet — start from one above{mayDraft ? ', or New automation for a blank one' : ''}.
          </div>
        )}
        {rows.map((row) => {
          const Art = ART[row.trigger];
          const chip = STATUS_CHIP[row.status];
          const inbox = inboxes.find((candidate) => candidate.id === row.inboxId);
          const problem = automationProblem(row, inbox ? needsReauth(inbox) : false);
          const sms = row.channel === 'sms';
          const totals = row.totals;
          const sent = totals?.sent ?? 0;
          const tally = bookedOf(row.id);
          return (
            <article key={row.id} className={`auto-card${problem ? ' has-problem' : ''}`}>
              <div className="auto-card-main">
                <Art className="auto-thumb" />
                <div className="auto-card-text">
                  <button type="button" className="auto-name" onClick={() => onOpen({ id: row.id, name: row.name })}>
                    {row.name}
                  </button>
                  <div className="auto-muted">
                    {triggerLine(row)} · {sms ? 'Text' : 'Email'} ·{' '}
                    {row.audienceType === 'affiliates' ? 'Affiliates' : 'Customers'}
                  </div>
                  <div className="auto-sub">{lastRunLine(row, since, nameOf)}</div>
                </div>
                <div className="auto-stats">
                  <div className="auto-stat">
                    <b>{sent.toLocaleString()}</b>
                    <span>sent</span>
                  </div>
                  <div className="auto-stat">
                    <b>{sms || !sent ? '—' : `${Math.round(((totals?.opened ?? 0) / sent) * 100)}%`}</b>
                    <span>{sms ? 'no opens on text' : 'opened'}</span>
                  </div>
                  <div className="auto-stat">
                    <b>{(totals?.replied ?? 0).toLocaleString()}</b>
                    <span>replied</span>
                  </div>
                  <div
                    className="auto-stat"
                    title={
                      tally && !tally.checked
                        ? `Couldn’t check: ${tally.reason ?? 'dispatch could not be read'}`
                        : 'Trips booked within 7 days of a send — what followed, not proof'
                    }
                  >
                    <b className={tally && !tally.checked ? 'auto-unknown' : undefined}>
                      {booked.error ? '?' : !tally ? '…' : tally.checked ? tally.bookings.toLocaleString() : '?'}
                    </b>
                    <span>booked again</span>
                  </div>
                </div>
                <div className="auto-toggle">
                  <span className="cchip" style={{ background: chip.bg, color: chip.fg }}>
                    {chip.label}
                  </span>
                  {maySend && (
                    <Switch
                      on={row.status === 'active'}
                      label={`${row.name} is ${row.status === 'active' ? 'on' : 'off'}`}
                      disabled={toggling !== null}
                      onToggle={() => void toggle(row)}
                    />
                  )}
                </div>
              </div>
              {toggleError?.id === row.id && <div className="auto-toggle-error">{toggleError.message}</div>}
              {problem && (
                <ProblemBanner
                  automation={row}
                  problem={problem}
                  inbox={inbox}
                  onOpenRun={onOpenRun}
                  onFixed={() => setNonce((n) => n + 1)}
                />
              )}
            </article>
          );
        })}
      </section>
    </div>
  );
}

type Step = 'when' | 'wait' | 'send' | 'always';

/** An automation's own page — loads it, then hands the form a fixed starting point. */
export function AutomationPage({
  open,
  onClose,
  onOpenRun,
}: {
  open: AutomationOpen;
  onClose: () => void;
  onOpenRun: (campaignId: number) => void;
}) {
  const { source } = useComms();
  const automationId = open.id;
  const loaded = useLoad(
    () => (automationId ? source.api.getAutomation(automationId) : Promise.resolve(null)),
    [source, automationId],
  );
  const existing = loaded.data;
  if (automationId && !existing) {
    return <div className="cmg-body">{loaded.error ? 'Could not load this automation.' : 'Loading…'}</div>;
  }
  const preset = open.id === null && open.preset ? AUTOMATION_PRESETS[open.preset] : null;
  return (
    <AutomationForm
      key={existing ? `${existing.id}:${existing.status}` : 'new'}
      existing={existing}
      preset={preset}
      onClose={onClose}
      onOpenRun={onOpenRun}
      reload={loaded.reload}
    />
  );
}

function AutomationForm({
  existing,
  preset,
  onClose,
  onOpenRun,
  reload,
}: {
  existing: CampaignAutomation | null;
  /** A new automation's starting point; ignored for one that exists. */
  preset: AutomationPreset | null;
  onClose: () => void;
  onOpenRun: (campaignId: number) => void;
  reload: () => void;
}) {
  const { source, inboxes, agents } = useComms();
  const { fmtDate, fmtDateTime, zoneLabel } = useTimeFormat();
  const { maySend } = useAutomationRights();
  const start = existing ? null : preset;
  const [step, setStep] = useState<Step>('when');
  const [name, setName] = useState(existing?.name ?? start?.name ?? '');
  const [trigger, setTrigger] = useState<AutomationTrigger>(
    existing?.trigger ?? start?.trigger ?? 'trip_completed',
  );
  const [delayHours, setDelayHours] = useState(existing?.delayHours ?? start?.delayHours ?? 24);
  const [lapsedDays, setLapsedDays] = useState(existing?.lapsedDays ?? 90);
  const [audienceType, setAudienceType] = useState(existing?.audienceType ?? 'customers');
  const [channel, setChannel] = useState<CampaignChannel>(existing?.channel ?? 'email');
  const [inboxId, setInboxId] = useState<number | null>(existing?.inboxId ?? null);
  const [subject, setSubject] = useState(existing?.subject ?? start?.subject ?? '');
  const [body, setBody] = useState<RichTextValue>({
    html: existing?.bodyHtml ?? start?.bodyHtml ?? '',
    text: existing?.bodyText ?? '',
  });
  const [bodySms, setBodySms] = useState(existing?.bodySms ?? '');
  const [quietOn, setQuietOn] = useState(existing ? existing.sendWindowStart != null : true);
  const [windowStart, setWindowStart] = useState(existing?.sendWindowStart ?? 8 * 60);
  const [windowEnd, setWindowEnd] = useState(existing?.sendWindowEnd ?? 21 * 60);
  const [frequencyCap, setFrequencyCap] = useState<number | null>(existing?.frequencyCapHours ?? 72);
  const [trackClicks, setTrackClicks] = useState(existing?.trackClicks ?? true);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<AutomationPreview | 'failed' | null>(null);

  const status = existing?.status ?? 'draft';
  const locked = status === 'active';
  const sms = channel === 'sms';
  const senders = inboxes.filter((inbox) =>
    sms
      ? (inbox.channelType === 'twilio' && inbox.channel?.medium !== 'whatsapp') || inbox.channelType === 'ringcentral'
      : inbox.channelType === 'email',
  );
  const sender = inboxes.find((inbox) => inbox.id === inboxId);
  const since = (iso: string | null) => sinceLabel(iso, (value) => fmtDate(value));
  const nameOf = (id: number) => agents.find((agent) => agent.dashBoardId === id)?.name ?? null;

  const draft = (): AutomationDraft => ({
    name: name.trim(),
    trigger,
    delayHours,
    lapsedDays: trigger === 'lapsed' ? lapsedDays : null,
    audienceType,
    ...(existing ? {} : { channel }),
    inboxId,
    subject: sms ? null : subject,
    bodyHtml: sms ? null : body.html,
    bodyText: sms ? null : body.text.trim() || null,
    bodySms: sms ? bodySms : null,
    sendWindowStart: quietOn ? windowStart : null,
    sendWindowEnd: quietOn ? windowEnd : null,
    frequencyCapHours: frequencyCap,
    trackClicks: !sms && trackClicks,
  });

  const run = async (label: string, action: () => Promise<unknown>, close = false) => {
    setBusy(label);
    setError(null);
    try {
      await action();
      if (close) onClose();
      else reload();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Something went wrong.');
    } finally {
      setBusy(null);
    }
  };

  const save = async () => {
    if (existing) return source.api.updateAutomation(existing.id, draft());
    return source.api.createAutomation(draft());
  };

  const loadPreview = async () => {
    setPreview(null);
    try {
      setPreview(await source.api.previewAutomation({ trigger, delayHours, lapsedDays, audienceType, channel }));
    } catch {
      setPreview('failed');
    }
  };

  const problem = existing ? automationProblem(existing, sender ? needsReauth(sender) : false) : null;
  const chip = STATUS_CHIP[status];
  const ready = Boolean(name.trim() && inboxId);
  const statusLine = !existing
    ? 'Nothing sends until somebody with Send switches it on.'
    : status === 'active'
      ? `On since ${fmtDateTime(existing.activatedAt)} · checked every 15 minutes. Pause it to change it — what it sends is what was approved when it was switched on.`
      : status === 'paused'
        ? `${lastRunLine(existing, since, nameOf)}. Switching it on again starts from that moment — nobody who qualified while it was paused is messaged later.`
        : 'Not switched on yet — nothing sends until somebody with Send switches it on.';
  const hasCopy = sms ? bodySms.trim() !== '' : subject.trim() !== '' && body.html.trim() !== '';
  const Art = ART[trigger];
  const SendIcon = sms ? MessageSquare : Mail;

  const previewRow = (
    <div className="auto-preview">
      <button type="button" className="cbtn" onClick={() => void loadPreview()}>
        How many would this reach?
      </button>
      <span>
        {preview === 'failed' ? (
          'Could not read dispatch to count.'
        ) : preview ? (
          <>
            <b>
              {preview.people.toLocaleString()} {preview.people === 1 ? 'person' : 'people'}
            </b>{' '}
            would have qualified in the last {preview.days} days
            {preview.sample.length ? ` — e.g. ${preview.sample.join(', ')}` : ''}. Nothing is sent by asking.
          </>
        ) : (
          'Counts the last 30 days with this trigger. Nothing is sent.'
        )}
      </span>
    </div>
  );

  return (
    <div className="auto-page">
      <header className="auto-head">
        <div className="auto-head-row">
          <input
            className="auto-title"
            aria-label="Name"
            placeholder="Name this automation"
            value={name}
            maxLength={200}
            disabled={locked}
            onChange={(e) => setName(e.target.value)}
          />
          <span className="cchip" style={{ background: chip.bg, color: chip.fg }}>
            {chip.label}
          </span>
          <div style={{ flex: 1 }} />
          {existing && !locked && (
            <button
              type="button"
              className="cbtn"
              disabled={busy !== null}
              onClick={() => void run('delete', () => source.api.deleteAutomation(existing.id), true)}
            >
              <Trash2 size={13} />
              Delete
            </button>
          )}
          {!locked && (
            <button
              type="button"
              className="cbtn"
              disabled={busy !== null || !ready}
              title={ready ? undefined : 'Give it a name and pick who it is from'}
              onClick={() => void run('save', save, true)}
            >
              {busy === 'save' ? 'Saving…' : 'Save'}
            </button>
          )}
          {!locked && maySend && (
            <button
              type="button"
              className="cbtn cbtn-primary"
              disabled={busy !== null || !ready}
              title="Saves it, then switches it on as you"
              onClick={() =>
                void run(
                  'activate',
                  async () => {
                    const saved = await save();
                    await source.api.activateAutomation(saved.id);
                  },
                  true,
                )
              }
            >
              <Play size={13} />
              {busy === 'activate' ? 'Switching on…' : 'Save and switch on'}
            </button>
          )}
          {locked && maySend && existing && (
            <button
              type="button"
              className="cbtn"
              disabled={busy !== null}
              onClick={() => void run('pause', () => source.api.pauseAutomation(existing.id))}
            >
              <Pause size={13} />
              {busy === 'pause' ? 'Pausing…' : 'Pause'}
            </button>
          )}
        </div>
        <p className="auto-head-line">
          {statusLine}
          {!maySend && ' Switching an automation on needs Campaigns at Send.'}
        </p>
      </header>

      {existing && problem && (
        <ProblemBanner
          automation={existing}
          problem={problem}
          inbox={sender}
          onOpenRun={onOpenRun}
          onFixed={reload}
        />
      )}
      {error && <div className="cmg-insight bad">{error}</div>}

      <div className="auto-editor">
        <section className="auto-flow" aria-label="The flow">
          <button
            type="button"
            className={`auto-node${step === 'when' ? ' on' : ''}`}
            aria-pressed={step === 'when'}
            onClick={() => setStep('when')}
          >
            <Art className="auto-node-art" />
            <span>
              <span className="auto-eyebrow accent">When</span>
              <b>{WHEN_TITLES[trigger]}</b>
              <span className="auto-muted">{whenDetail(trigger, audienceType)}</span>
            </span>
          </button>
          <div className="auto-link" aria-hidden />
          <button
            type="button"
            className={`auto-node${step === 'wait' ? ' on' : ''}`}
            aria-pressed={step === 'wait'}
            onClick={() => setStep('wait')}
          >
            <span className="auto-node-icon">
              <Clock size={18} />
            </span>
            <span>
              <span className="auto-eyebrow">Wait</span>
              <b>{waitTitle({ trigger, delayHours, lapsedDays })}</b>
            </span>
          </button>
          <div className="auto-link" aria-hidden />
          <button
            type="button"
            className={`auto-node${step === 'send' ? ' on' : ''}`}
            aria-pressed={step === 'send'}
            onClick={() => setStep('send')}
          >
            <span className="auto-node-icon solid">
              <SendIcon size={18} />
            </span>
            <span>
              <span className="auto-eyebrow">{sms ? 'Send text' : 'Send email'}</span>
              <b className={hasCopy ? undefined : 'auto-missing'}>
                {!hasCopy
                  ? 'Write the message'
                  : sms
                    ? `“${bodySms.length > 70 ? `${bodySms.slice(0, 70)}…` : bodySms}”`
                    : `“${subject}”`}
              </b>
              <span className={sender ? 'auto-muted' : 'auto-muted auto-missing'}>
                {sender ? `From ${identityLine(sender) ?? sender.name}` : `Pick the ${sms ? 'number' : 'mailbox'} it is from`}
                {sender && !sms && trackClicks ? ' · clicks counted' : ''}
              </span>
            </span>
          </button>
          <div className="auto-link" aria-hidden />
          <button
            type="button"
            className={`auto-node${step === 'always' ? ' on' : ''}`}
            aria-pressed={step === 'always'}
            onClick={() => setStep('always')}
          >
            <span className="auto-node-icon ok">
              <ShieldCheck size={18} />
            </span>
            <span>
              <span className="auto-eyebrow">Always</span>
              <b>
                {alwaysTitle({
                  sendWindowStart: quietOn ? windowStart : null,
                  sendWindowEnd: quietOn ? windowEnd : null,
                  frequencyCapHours: frequencyCap,
                })}
              </b>
              <span className="auto-muted">Opted-out people are never messaged</span>
            </span>
          </button>
        </section>

        <section className="auto-panel" aria-label="Settings for this step">
          <fieldset disabled={locked} className="auto-fieldset">
            {step === 'when' && (
              <>
                <div>
                  <span className="auto-eyebrow accent">Editing · When</span>
                  <h2>What sets it off</h2>
                </div>
                <div className="auto-triggers" role="radiogroup" aria-label="Trigger">
                  {TRIGGERS.map(({ key, label, hint, art: TriggerArt }) => (
                    <button
                      key={key}
                      type="button"
                      role="radio"
                      aria-checked={trigger === key}
                      className={`auto-trigger${trigger === key ? ' on' : ''}`}
                      onClick={() => setTrigger(key)}
                    >
                      <TriggerArt className="auto-trigger-art" />
                      <b>{label}</b>
                      <span className="auto-muted">{hint}</span>
                    </button>
                  ))}
                </div>
                <div className="cmg-field">
                  <span className="cmg-field-label">Who</span>
                  <div className="auto-seg" role="group" aria-label="Who">
                    {(['customers', 'affiliates'] as const).map((value) => (
                      <button
                        key={value}
                        type="button"
                        className={audienceType === value ? 'on' : undefined}
                        aria-pressed={audienceType === value}
                        onClick={() => setAudienceType(value)}
                      >
                        {value === 'customers' ? 'Customers' : 'Affiliates (reservation desks)'}
                      </button>
                    ))}
                  </div>
                </div>
                {previewRow}
              </>
            )}

            {step === 'wait' && (
              <>
                <div>
                  <span className="auto-eyebrow accent">Editing · Wait</span>
                  <h2>{trigger === 'lapsed' ? 'How long counts as quiet' : 'How long to wait'}</h2>
                </div>
                {trigger === 'lapsed' ? (
                  <div className="cmg-field" style={{ maxWidth: 280 }}>
                    <span className="cmg-field-label">Days since their last trip</span>
                    <input
                      className="cmg-input"
                      type="number"
                      min={7}
                      max={730}
                      value={lapsedDays}
                      onChange={(e) => setLapsedDays(Number(e.target.value) || 90)}
                    />
                    <span className="cmg-hint">
                      Once, when their last trip is this old and nothing is booked ahead. Somebody who comes
                      back and goes quiet again is won back again.
                    </span>
                  </div>
                ) : (
                  <div className="cmg-field" style={{ maxWidth: 280 }}>
                    <span className="cmg-field-label">
                      {trigger === 'trip_completed' ? 'Hours after the pickup' : 'Hours after the reservation'}
                    </span>
                    <input
                      className="cmg-input"
                      type="number"
                      min={0}
                      max={720}
                      value={delayHours}
                      onChange={(e) => setDelayHours(Math.max(0, Number(e.target.value) || 0))}
                    />
                    <span className="cmg-hint">
                      0 sends it at the next check after it happens — within 15 minutes. Sending hours (under
                      Always) can hold it until morning.
                    </span>
                  </div>
                )}
                {previewRow}
              </>
            )}

            {step === 'send' && (
              <>
                <div>
                  <span className="auto-eyebrow accent">Editing · Send</span>
                  <h2>What it sends</h2>
                </div>
                <div className="cmg-row">
                  <div className="cmg-field">
                    <span className="cmg-field-label">Send as</span>
                    <select
                      className="cmg-input"
                      value={channel}
                      disabled={Boolean(existing)}
                      title={existing ? 'Fixed once saved — the people are addressed by a different column' : undefined}
                      onChange={(e) => {
                        setChannel(e.target.value as CampaignChannel);
                        setInboxId(null);
                      }}
                    >
                      <option value="email">Email</option>
                      <option value="sms">Text message</option>
                    </select>
                  </div>
                  <div className="cmg-field">
                    <span className="cmg-field-label">{sms ? 'From number' : 'From mailbox'}</span>
                    <select
                      className="cmg-input"
                      value={inboxId ?? ''}
                      onChange={(e) => setInboxId(e.target.value ? Number(e.target.value) : null)}
                    >
                      <option value="">Pick one…</option>
                      {senders.map((inbox) => (
                        <option key={inbox.id} value={inbox.id}>
                          {identityLine(inbox) ?? inbox.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                {sms ? (
                  <div className="cmg-field">
                    <span className="cmg-field-label">Message</span>
                    <textarea
                      className="cmg-input"
                      rows={4}
                      value={bodySms}
                      onChange={(e) => setBodySms(e.target.value)}
                    />
                    <span className="cmg-hint">Merge tags like {'{{name}}'} work here, as in any campaign.</span>
                  </div>
                ) : (
                  <>
                    <div className="cmg-field">
                      <span className="cmg-field-label">Subject</span>
                      <input className="cmg-input" value={subject} onChange={(e) => setSubject(e.target.value)} />
                    </div>
                    <div className="cmg-field">
                      <span className="cmg-field-label">Email</span>
                      <RichText
                        value={body}
                        onChange={setBody}
                        placeholder="Hi {{name}}, thank you for riding with us…"
                        disabled={locked}
                      />
                    </div>
                    <label className="cmg-member" style={{ cursor: 'pointer' }}>
                      <input type="checkbox" checked={trackClicks} onChange={() => setTrackClicks((on) => !on)} />
                      <span className="cmg-member-name">Count who clicks each link</span>
                    </label>
                  </>
                )}
              </>
            )}

            {step === 'always' && (
              <>
                <div>
                  <span className="auto-eyebrow accent">Editing · Always</span>
                  <h2>Rules every send keeps</h2>
                </div>
                <label className="cmg-member" style={{ cursor: 'pointer' }}>
                  <input type="checkbox" checked={quietOn} onChange={() => setQuietOn((on) => !on)} />
                  <span className="cmg-member-name">Only send during these hours</span>
                </label>
                {quietOn && (
                  <div className="cmg-row">
                    <div className="cmg-field">
                      <span className="cmg-field-label">From</span>
                      <input
                        className="cmg-input"
                        type="time"
                        value={minutesToClock(windowStart)}
                        onChange={(e) => setWindowStart(clockToMinutes(e.target.value) ?? windowStart)}
                      />
                    </div>
                    <div className="cmg-field">
                      <span className="cmg-field-label">Until{zoneLabel ? ` (${zoneLabel})` : ''}</span>
                      <input
                        className="cmg-input"
                        type="time"
                        value={minutesToClock(windowEnd)}
                        onChange={(e) => setWindowEnd(clockToMinutes(e.target.value) ?? windowEnd)}
                      />
                    </div>
                  </div>
                )}
                <div className="cmg-field" style={{ maxWidth: 420 }}>
                  <span className="cmg-field-label">Skip people who recently got another campaign</span>
                  <select
                    className="cmg-input"
                    value={frequencyCap ?? ''}
                    onChange={(e) => setFrequencyCap(e.target.value ? Number(e.target.value) : null)}
                  >
                    <option value="">Don’t skip anyone</option>
                    {FREQUENCY_CAPS.map((cap) => (
                      <option key={cap.hours} value={cap.hours}>
                        Within the last {cap.label}
                      </option>
                    ))}
                  </select>
                </div>
                <p className="cmg-hint">
                  Whatever is set here, nobody who opted out is ever messaged, and the{' '}
                  {sms ? 'number’s' : 'mailbox’s'} daily allowance keeps part of the day for replies.
                </p>
              </>
            )}
          </fieldset>
        </section>
      </div>

      {existing && <AutomationRuns automation={existing} onOpenRun={onOpenRun} />}
    </div>
  );
}

const RUN_CHIP: Partial<Record<AutomationRun['status'], { bg: string; fg: string; label: string }>> = {
  completed: { bg: 'var(--c-green-muted)', fg: 'var(--c-green)', label: 'Sent' },
  sending: { bg: 'var(--c-blue-muted)', fg: 'var(--c-blue)', label: 'Sending' },
  stopped: { bg: 'var(--c-orange-muted)', fg: 'var(--c-orange)', label: 'Stopped' },
  failed: { bg: 'var(--c-red-muted)', fg: 'var(--c-red)', label: 'Failed' },
};

/** Every run, newest first — each an ordinary campaign with its own results. */
function AutomationRuns({
  automation,
  onOpenRun,
}: {
  automation: CampaignAutomation;
  onOpenRun: (campaignId: number) => void;
}) {
  const { source } = useComms();
  // Date AND clock: runs go out every quarter hour, so a weekday alone names six of them.
  const { fmtDateTime } = useTimeFormat();
  const runs = automation.runs ?? [];
  const sms = automation.channel === 'sms';
  const askIds = runs.filter((run) => (run.stats?.sent ?? 0) > 0).map((run) => run.id);
  const booked = useLoad(
    () => (askIds.length ? source.api.getCampaignBookingCounts(askIds) : Promise.resolve([])),
    [source, askIds.join(',')],
  );
  const totals = automation.totals;
  const sent = totals?.sent ?? 0;
  return (
    <section className="auto-runs" aria-label="Runs">
      <div className="auto-section-head">
        <h2>Runs</h2>
        <span>
          Each run is an ordinary campaign with its own results · {sent.toLocaleString()} sent
          {!sms && sent ? `, ${Math.round(((totals?.opened ?? 0) / sent) * 100)}% opened` : ''},{' '}
          {(totals?.replied ?? 0).toLocaleString()} replied
        </span>
      </div>
      {runs.length === 0 ? (
        <div className="auto-empty">
          No runs yet — the first goes out at the first check that finds somebody who qualifies.
        </div>
      ) : (
        <div className="chome-scroll">
          <div className="ctable auto-runs-table">
            <div className="ctable-head">
              <div className="auto-run-when">RUN</div>
              <div className="chome-col-num">SENT</div>
              <div className="chome-col-num">OPENED</div>
              <div className="chome-col-num">REPLIED</div>
              <div className="chome-col-num" title="Trips booked within 7 days of receiving it">
                BOOKED
              </div>
              <div className="auto-run-status">STATUS</div>
            </div>
            {runs.map((run, index) => {
              const chip = RUN_CHIP[run.status] ?? { bg: 'var(--c-secondary)', fg: 'var(--c-muted)', label: run.status };
              const count = booked.data?.find((row) => row.id === run.id);
              const stats = run.stats;
              return (
                <button
                  key={run.id}
                  type="button"
                  className={`ctable-row clickable${index % 2 === 1 ? ' odd' : ''}`}
                  title="Open this run’s results"
                  onClick={() => onOpenRun(run.id)}
                >
                  <div className="auto-run-when">{fmtDateTime(run.startedAt ?? run.createdAt ?? null)}</div>
                  <div className="chome-col-num">{(stats?.sent ?? 0).toLocaleString()}</div>
                  <div className="chome-col-num">{sms ? '—' : (stats?.opened ?? 0).toLocaleString()}</div>
                  <div className="chome-col-num">{(stats?.replied ?? 0).toLocaleString()}</div>
                  <div className="chome-col-num">
                    {!stats?.sent ? (
                      '—'
                    ) : !count ? (
                      '…'
                    ) : count.checked ? (
                      count.bookings.toLocaleString()
                    ) : (
                      <span className="chome-unknown" title={`Couldn’t check: ${count.reason ?? ''}`}>
                        ?
                      </span>
                    )}
                  </div>
                  <div className="auto-run-status">
                    <span className="cchip" style={{ background: chip.bg, color: chip.fg }}>
                      {chip.label}
                    </span>
                    {run.error && <span className="auto-run-error">{run.error}</span>}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </section>
  );
}
